import type { CardTextProps } from '@Components/BigBrowse/GridCardMesh/Components/Texts/CardText';
import type { GridCard } from '@Components/BigBrowse/Model/Classes/GridCard';
import { getGap } from '@Components/BigBrowse/Model/Classes/GridCard';
import type {
  PlaneSize,
  PlaneTypes,
  Position2D,
} from '@Components/BigBrowse/Model/ModelTypes';
import { getThreeUpCard } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/threeUp/helpers/GetThreeUpCard';
import { getTextBlockSize } from '@Utils/3d';
import { Assets } from '@Utils/AssetsStore';
import { interpolate } from '@Utils/miscUtils';
import { Dimensions } from 'react-native';

import type {
  BrandConcessionContentDM,
  ThreeUpImage,
  ThreeUpStyles,
  ThreeUpView,
} from '../../common/MultiSectionView.types';
import { getColumnWidth } from '../../merchandiser/helpers/MerchandiserCommon';

export const TEXT_PART_TOP_PADDING = 50;
const LINE_HEIGHT = 18;
const FONT_SIZE = 14;

const FIGMA_BOX_MIN: PlaneSize = { width: 375, height: 291 + 45 + 36 }; // = 372
const FIGMA_BOX_MAX: PlaneSize = { width: 393, height: 430 + 55 + 71 }; // = 556

const FIGMA_GROUP_BOX_MIN: PlaneSize = { width: 314, height: 291 };
const FIGMA_GROUP_BOX_MAX: PlaneSize = { width: 352, height: 430 };

export const layoutThreeUpPortrait = async ({
  section,
  layoutTable,
}: {
  section: ThreeUpView;
  layoutTable: GridCard<PlaneTypes>[][];
}) => {
  const styles = await getBoxLayoutPortraitThreeUp(section);

  layoutTable.forEach((column, index) => {
    column.push(
      index === 0
        ? getThreeUpCard(section, styles)
        : getGap({ width: getColumnWidth(), height: styles!.boxSize.height })
    );
  });
};

export const getBoxLayoutPortraitThreeUp = async (
  section: ThreeUpView
): Promise<ThreeUpStyles> => {
  const contentPosition = { x: 0, y: 0 };
  const { width, height } = Dimensions.get('window');

  const boxSize: PlaneSize = { width, height };
  const contentBoxSize = boxSize;

  const textPartWidth = (324 / 375) * contentBoxSize.width;

  const { paragraphs, paragraphSizes = [] } = await getParagraphs({
    section,
    maxWidth: textPartWidth,
  });

  const moreCta = getMoreCta();
  const textPartSize = getTextPartSize(paragraphSizes, section);
  const textPartPosition = getTextPartPosition(contentBoxSize, textPartSize);

  const textPart: ThreeUpStyles['textPart'] = {
    size: textPartSize,
    textContentsContainer: textPartSize,
    position: textPartPosition,
    paragraphs,
    paragraphSizes,
    moreCta,
  };

  const imagesPart = getImagesPart({
    boxSize,
    textPart: textPartSize,
    section,
  });

  return {
    boxSize,
    contentBoxSize,
    contentPosition,
    textPart,
    imagesPart,
    layout: 'portrait',
  };
};

const getParagraphs = async ({
  section,
  maxWidth,
}: {
  section: ThreeUpView;
  maxWidth: number;
}): Promise<
  Pick<ThreeUpStyles['textPart'], 'paragraphs' | 'paragraphSizes'>
> => {
  const removedDoubledLines = section.dtoData.text.replace(/\n\n+/g, '\n');

  const paragraphArray = removedDoubledLines.split('\n');

  const paragraphs: CardTextProps[] = paragraphArray.map((paragraph, index) => {
    const isBold = index === 0 && paragraphArray.length > 1;
    const fontFamily = isBold
      ? Assets.Font_Gilroy_Bold
      : Assets.Font_Gilroy_Medium;

    const props: CardTextProps = {
      text: paragraph,
      fontAsset: fontFamily,
      fontSize: FONT_SIZE,
      maxWidth,
      color: section.dtoData.fontColour ?? 'black',
      lineHeight: LINE_HEIGHT / FONT_SIZE,
      letterSpacing: 0.03,
    };

    return props;
  });

  const paragraphSizes = await Promise.all(
    paragraphs.map((paragraph) => getTextBlockSize(paragraph))
  );

  return { paragraphs, paragraphSizes };
};

const getTextPartSize = (
  paragraphSizes: PlaneSize[],
  section: ThreeUpView
): PlaneSize => {
  const maxWidth = paragraphSizes.reduce(
    (acc, size) => Math.max(acc, size.width),
    0
  );

  const paragraphsTotalHeight = paragraphSizes.reduce(
    (acc, size) => acc + size.height,
    0
  );

  const ctaMoreHeight =
    section.dtoData.linkButtonUrl && section.dtoData.linkButtonTitle
      ? getMoreCta().boxSize.height + 20
      : 0;

  const totalHeight =
    TEXT_PART_TOP_PADDING +
    paragraphsTotalHeight +
    Math.max(paragraphSizes.length - LINE_HEIGHT, 0) +
    ctaMoreHeight +
    20;

  return { width: maxWidth, height: totalHeight };
};

const getTextPartPosition = (
  contentBoxSize: PlaneSize,
  textContentContainer: PlaneSize
): Position2D => ({
  x: 0,
  y: Math.round((contentBoxSize.height - textContentContainer.height) / 2),
});

const getMoreCta = (): ThreeUpStyles['textPart']['moreCta'] => ({
  textProps: {
    text: 'Find out more',
    fontSize: 14,
    fontAsset: Assets.Font_Gilroy_SemiBold,
    lineHeight: 17 / 14,
    letterSpacing: 0.03,
  },
  boxSize: { height: 45, width: 155 },
});

const getImagesPart = ({
  boxSize,
  textPart,
  section,
}: {
  boxSize: PlaneSize;
  textPart: PlaneSize;
  section: ThreeUpView;
}): ThreeUpStyles['imagesPart'] => {
  const { width } = boxSize;
  const height = boxSize.height - textPart.height;
  const size = { width, height };

  const y = -boxSize.height / 2 + height / 2;

  const groupSize = getImagesGroupBoxSize(size);

  return {
    size,
    position: { x: 0, y },
    imagesGroupBox: { size: groupSize },
    images: getImages(section, groupSize),
  };
};

const getImagesGroupBoxSize = (parentBox: PlaneSize): PlaneSize => {
  // make sure that they grow based on Figma designs that spread images on x and y
  const width = interpolate(parentBox.width, {
    input: [FIGMA_BOX_MIN.width, FIGMA_BOX_MAX.width],
    output: [FIGMA_GROUP_BOX_MIN.width, FIGMA_GROUP_BOX_MAX.width],
  });

  const height = interpolate(parentBox.height, {
    input: [FIGMA_BOX_MIN.height, FIGMA_BOX_MAX.height],
    output: [FIGMA_GROUP_BOX_MIN.height, FIGMA_GROUP_BOX_MAX.height],
  });

  return {
    // make sure that spreading does not exceed bounding box
    width: Math.min(width, parentBox.width - 60),
    height: Math.min(height, parentBox.height - 100),
  };
};

const getImages = (
  section: ThreeUpView,
  groupBoxSize: PlaneSize
): ThreeUpImage[] =>
  section.dtoData.products.slice(0, 3).map((product, index) =>
    getThreeUpImage({
      product,
      definition: threeUpDefinitions[index],
      groupBoxSize,
    })
  );

type ThreeUpImageDefinition = [number, number, number]; // [size, top, left]
const threeUpDefinitions: ThreeUpImageDefinition[] = [
  [194, 96, 10],
  [124, 52, 180],
  [79, 0, 89],
];

const getThreeUpImage = ({
  product,
  definition,
  groupBoxSize,
}: {
  // @ts-expect-error
  product: BrandConcessionContentDM['header']['products'][0];
  definition: ThreeUpImageDefinition;
  groupBoxSize: PlaneSize;
}): ThreeUpImage => {
  const [defSize, top, left] = definition;

  const size = groupBoxSize.width * (defSize / FIGMA_GROUP_BOX_MIN.width);
  const y = // center of image based on center of group and its top distance from the top of the group
    groupBoxSize.height / 2 -
    groupBoxSize.height * (top / FIGMA_GROUP_BOX_MIN.height) -
    size / 2;

  const x =
    -groupBoxSize.width / 2 +
    groupBoxSize.width * (left / FIGMA_GROUP_BOX_MIN.width) +
    size / 2;

  return { size, position: { x, y }, dto: product };
};
